import React, { createContext, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

/**
 * SeoContext provides per-page SEO overrides fetched from the backend
 * (/api/seo). If the API is unreachable the context simply stays empty
 * and every <SeoHead> falls back to its hardcoded `defaults` prop —
 * so the marketing site keeps shipping correct meta even with the
 * backend down.
 *
 * Each page in /app/frontend/src/pages/* renders <SeoHead pageKey="..."
 * defaults={{ title, description, keywords, ogImage, canonical }} />
 * instead of a raw <Helmet>. The defaults match what was originally
 * hardcoded so production behaviour is unchanged before the admin makes
 * any edits.
 */

const SeoContext = createContext({ map: {}, ready: false, refresh: () => {} });

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const SeoProvider = ({ children }) => {
  const [map, setMap] = useState({});
  const [ready, setReady] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/seo`, { cache: "no-store" });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const list = await res.json();
      const next = {};
      for (const item of list) next[item.page_key] = item;
      setMap(next);
    } catch (err) {
      // Stay silent — defaults will be used.
      // eslint-disable-next-line no-console
      console.warn("SEO config fetch failed, using hardcoded defaults:", err.message);
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <SeoContext.Provider value={{ map, ready, refresh: load }}>
      {children}
    </SeoContext.Provider>
  );
};

/** Hook returning the merged SEO config for a page (overrides ?? defaults). */
export const useSeo = (pageKey, defaults = {}) => {
  const { map } = useContext(SeoContext);
  const override = map[pageKey] || {};
  return {
    title:       override.title       || defaults.title       || "Kairoo Technologies",
    description: override.description || defaults.description || "",
    keywords:    override.keywords    || defaults.keywords    || "",
    ogImage:     override.og_image    || defaults.ogImage     || "https://kairootechnologies.com/og-image.png",
    canonical:   override.canonical   || defaults.canonical   || "",
  };
};

/**
 * Render-ready Helmet wrapper. Plug into any page in place of a raw <Helmet>.
 * `extra` slot lets pages add JSON-LD, twitter tags, robots meta, etc.
 */
export const SeoHead = ({ pageKey, defaults, extra = null }) => {
  const seo = useSeo(pageKey, defaults);
  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      {seo.canonical && <link rel="canonical" href={seo.canonical} />}
      <meta property="og:type" content="website" />
      {seo.canonical && <meta property="og:url" content={seo.canonical} />}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      {seo.ogImage && <meta name="twitter:image" content={seo.ogImage} />}
      {extra}
    </Helmet>
  );
};

export default SeoHead;
